/**
 * ⚠️ SHARED WITH MY-ORDERS v2 (docs/MY_ORDERS_V2_PLAN.md): `packages/core/myOrders/use*` dispatch
 * these workers, and `myOrders/buckets.test.ts` holds the captured `sale/filter` payloads. Run
 * `pnpm test` after ANY change here; a payload or flow change v1 wants and v2 cannot take is a
 * plan §10 conversation, not a quiet edit.
 *
 * Uplifted from `redux-store/saga/myOrder` (apps/web 750 lines, apps/mobile 567) — the
 * online-orders domain: fetching each order bucket, fulfillment updates, rider assignment,
 * void/unvoid, and the aggregator/chat integrations.
 *
 * The biggest saga on this branch. 36 symbols, classified mechanically rather than by eye:
 * **6 identical (here), 19 divergent, 9 web-only, 1 mobile-only.**
 *
 * ✅ **RESOLVED — the eight order-bucket fetchers are shared now.** They used to sit in the
 * variants over ONE line: web passed `skipGlobalLoader`, mobile `hideLoader`, from differently
 * named payload keys, and each app's interceptor reads only its own. They now pass BOTH keys,
 * so each platform behaves exactly as before and the eight collapse into one definition. The
 * long note beside them explains why unifying the NAME is a separate decision.
 *
 * The rest of the divergence, in one place so nobody re-derives it:
 *
 * | worker | difference |
 * |---|---|
 * | `updateFulfillmentStatement`, `setUnvoidOrder`, `setUncancelledOrder` | the offline-sync write is COMMENTED OUT on mobile |
 * | `saveMyOrderSale` | web opens the back-order modal on an insufficient-stock error and records the offline sync; mobile does neither |
 * | ~~`soldOutItem`~~ | converged 2026-09-16 on web's body (the callback gets the response — the v2 reject sheet reads its `code`); mobile's one caller, `OutOfStockPopup`, ignores the argument |
 * | ~~`getSearchOrdersData`~~ | converged 2026-09-17 (D12); the nested `sendObj` unwrap went with its one caller at the phase-4 cutover |
 * | `setVoidOrder` | web merges the reason and return-data objects into the request body and takes the id from a different payload path |
 * | `getDeliveryPartnerDetails`, `handleCancelRider` | different failure `code` strings, and mobile stringifies its request body |
 * | `fetchMyOrderData` | mobile threads a loader flag through; formatting otherwise |
 * | `updateFulfillmentStatusSaga` | web sends an explicit loader flag |
 * | `root` | web registers far more watchers — see each variant |
 *
 * ⚠️ Phantom import dropped under rule 2(b): mobile's unused `take` effect. (`updateSyncedSale`
 * was a phantom too — referenced only from five commented-out lines — until D3 was taken on
 * 2026-09-16 and `index.native.ts` started calling it; see that file's header.)
 *
 * ⚠️ Bare folder imports are REQUIRED in this file (hazard 16) — it runs on both platforms.
 */

import { put, takeLatest } from "redux-saga/effects";
import moment from "moment";
// Explicit, not incidental: `getChatSupportUrls` calls `moment().tz(...)`, and plain `moment`
// has no `.tz`. Both apps depend on moment-timezone and other core modules import it, so it
// happens to be patched already — but relying on another module's side effect to load first is
// how this breaks later. Importing it here makes the dependency this file's own.
import "moment-timezone";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { notifyError, notifySuccess } from "../../../effects-bridge";
import { getErrorMessage } from "../../../utils/posErrors";
import {
  aggregatorDataUpdateSuccess,
  updateSoldOutSuccess,
  getChatSupportUrlSuccess,
  saveZomatoChatSupportList,
  getSearchOrders, getSearchOrdersSuccess,
  // The nine order-list request/success pairs — see LIST_FETCHERS.
  getOverviewOrderData, getOverviewOrderDataSuccess,
  getNewOrderData, getNewOrderDataSuccess,
  getPreparingOrderData, getPreparingOrderDataSuccess,
  getProcessedOrderData, getProcessedOrderDataSuccess,
  getDispatchedOrderData, getDispatchedOrderDataSuccess,
  getDeliveredOrderData, getDeliveredOrderDataSuccess,
  getCancelledOrderData, getCancelledOrderDataSuccess,
  getOpenOrderData, getOpenOrderDataSuccess,
  getPreparedOrderData, getPreparedOrderDataSuccess,
} from "../../slices/MyOrderSlice";
import type { StoreKey } from "../../../myOrders/buckets";
import { saveSaleData } from "../../slices/SaleSlice";

export function* getAggregatorDataUpdate(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.getCustomerDetails(),
      data: payload.dataToSend,
    });
    if (response) {
      payload.callback(response);
      yield put(aggregatorDataUpdateSuccess(response));
    }
  } catch (error: any) {
    console.log(error);
  }
}

export function* getHoldOrders(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.getMyOrders(),
      data: payload.dataToSend,
    });
    if (response) {
      payload.callback(response);
    }
  } catch (error: any) {
    console.log(error);
  }
}

export function* getCallBtn(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.getCustomerDetails(),
      data: payload.dataToSend,
    });
    if (response) {
      payload.callback(response);
    }
  } catch (error: any) {
    console.log(error);
  }
}

export function* swiggyCallBtn(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.swiggyCallApi(payload?.id),
    });
    if (response) {
      payload.callback(response);
    }
  } catch (error: any) {
    console.log(error);
  }
}

export function* getFeedBackLink(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.feedbackApi(),
      data: payload?.data || {},
    });
    if (response) {
      payload.callback(response);
    } else {
      payload.callback({
        code: "Feedback already captured for this sale",
      });
    }
  } catch (error: any) {
    console.log(error);
  }
}

export function* handleAssignRider(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.assignRiderApi(payload.mid),
      data: JSON.stringify(payload.data),
    });
    if (response && payload.callback) {
      payload.callback(response);
    } else {
      payload.callback(response);
    }
  } catch (error: any) {
    console.log(error);
  }
}

// ── Adopted from web ──────────────────────────────────────────────────────────────────────
// These nine were `index.desktop.ts`-only. Every one is `api.request` + `put` + a callback:
// no DOM, no Electron, no browser API. `notifyError`/`notifySuccess` come from the effects
// bridge, which BOTH apps wire in their `bootstrapCoreBridges`. DEMAND-DRIVEN divergence, so
// they are shared now and each platform's `root()` registers them.
//
// ⚠️ `getPreparedOrders` still passes only `skipGlobalLoader`, which the native interceptor
// does not read, so the global loader shows during a prepared-orders fetch on mobile. Left
// as-is on purpose: that is web's literal behaviour and this worker is an adoption, not a
// redesign. The eight bucket fetchers below take the both-keys approach instead, because
// there the flag was the ONLY thing keeping them apart.
//
// ⚠️ NOT UNIT-TESTED, and not for lack of trying. This file imports `utils/apiService`, whose
// graph reaches `redux-store`; the native store's persist backend is `react-native-mmkv`, a
// native module with no Node build, so `node --test` cannot load either half. See
// `scripts/test-resolve-hooks.mjs`. What IS proven mechanically: `endpoints-diff` (web's 177
// keys byte-identical, native gained exactly 4), both tsc programs, and `audit:uplift`.
// The rest needs a device.
//
// ⚠️ TWO KNOWN WEB BUGS, CARRIED VERBATIM. Flagged, not fixed — silently "improving" web
// during an adoption is exactly what the house rule forbids, and both now reach mobile too:
//
//  1. `getChatSupportUrls` calls `moment().tz('YYYY-MM-DDTHH:MM:SS')`. `.tz()` takes a TIMEZONE
//     NAME, not a format string, so `timeStamp` is not the formatted string it reads as.
//  2. `checkForZomatoChatSupportFn` leaves `chatSupportAccess` UNDEFINED when the response
//     carries a code other than `REST_ERR_AGGREGATOR_CUSTOM`, then dispatches it anyway. The
//     reducer appends unconditionally (`[...state.chatSupportAccess, action.payload]`), so an
//     `undefined` entry lands in the array on any other error code.

export function* generateAggregatorBillSaga(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.generateAggregatorBillApi(payload?.mid),
    });
    if (response?.code) {
      notifyError(getErrorMessage(response.code) || response.code);
    } else {
      notifySuccess("Bill generated successfully");
      if (payload?.callback) {
        payload.callback(response);
      }
    }
  } catch (error: any) {
    console.log(error);
    notifyError(getErrorMessage(error?.code) || error?.code || "Failed to generate bill");
  }
}

export function* getApprovalStatus(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.approvalStatus(),
      data: payload?.data || {},
    });
    if (response) {
      payload.callback(response);
    }
  } catch (error: any) {
    console.log(error);
  }
}

export function* getCustomerNumber(action: any): Generator<any, void, any> {
  const payload = action.payload
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.getCustomerDetails(),
      data: payload?.dataToSend || {},
    });
    if (response) {
      payload.callback(response);
    }
  }
  catch (error: any) {
    console.log(error);
  }
}

export function* cancelSwiggyOrders(action: any): Generator<any, void, any> {
  const payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.TABLEVIEW.cancelOrder(payload.saleId),
      data: payload?.dataToSend || {},
    });
    if (response) {
      payload.callback(response);
    }
  }
  catch (error: any) {
    console.log(error);
  }
}

export function* getsaleReturnAmount(action: any): Generator<any, void, any> {
  const payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.saleReturnApi(),
      data: payload?.dataToSend || {},
    });
    if (!response?.code) {
      yield put(saveSaleData(response))
      payload.callback(response);
    }
  }
  catch (error: any) {
    console.log(error);
  }
}

/**
 * Fetch the aggregator chat-support URL for an order.
 *
 * 🔴 **THIS UPLIFT FIXES A LIVE `ReferenceError`.** In the app source `response` is declared with
 * `let` INSIDE the `try`, and the `catch` block then calls `payload.callback(response)` — a
 * variable that is not in scope there. So whenever this request failed, the error handler itself
 * threw instead of invoking the caller's callback, and the caller was never told. In a `.js` file
 * nothing flagged it; as `.ts` it is `TS2304` and cannot be carried across unchanged.
 *
 * The declaration is hoisted above the `try` so the `catch` sees `undefined`, which is what the
 * code plainly intended. **This is a BEHAVIOUR CHANGE**: on a chat-support failure the callback
 * now fires (with `undefined`) instead of the saga blowing up. Same class as the printer saga's
 * `METHODS` bug in session 21, and resolved the same way the user chose then.
 */
export function* getChatSupportUrls(action: any): Generator<any, void, any> {
  const payload = action.payload;
  let response: any;
  try {
    response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.getChatSupportUrl(),
      data: payload?.dataToSend || {},
    });
    if (!response?.code) {
      const orderId = payload?.dataToSend?.orderId;
      const timeStamp = moment().tz('YYYY-MM-DDTHH:MM:SS');
      const responseObj = {
        orderId,
        timeStamp,
        url: response?.chat_url
      }
      yield put(getChatSupportUrlSuccess(responseObj))
    }
    payload.callback(response);
  }
  catch (error: any) {
    console.log(error);
    payload.callback(response);
  }
}

export function* checkForZomatoChatSupportFn(action: any): Generator<any, void, any> {
  const payload = action.payload;
  try {
    let response = yield api.request({
      method: "GET",
      url: ENDPOINT.MYORDER.checkChatSupportAccess(payload.outletId),
      hideLoader: false
    } as any);
    const {outletId, channelDefId} = payload;
    let chatSupportAccess;
    if (!response?.code) {
       chatSupportAccess = {
          outletId,
          channelDefId,
          isChatEnable: response.chatSupport
       }
    } else if (outletId && channelDefId && response?.code === "REST_ERR_AGGREGATOR_CUSTOM") {
       chatSupportAccess = {
          outletId,
          channelDefId,
          isChatEnable: false
       }
    }
    yield put(saveZomatoChatSupportList(chatSupportAccess))
  }
  catch (error: any) {
    console.log(error);
  }
}

export function* getItemImage(action: any): Generator<any, void, any> {
  const {mid,itemNumber,  callback} = action.payload;
  try {
    const response = yield api.request({
      method: "GET",
      url: `/sale/${mid}/${itemNumber}/external/image`,
    });
    if (!response?.code) {
      callback && callback(response);
    }
  }
  catch (error: any) {
    console.log(error);
  }
}

// ── The order-list fetchers: ONE worker, a table of nine ──────────────────────────────────
// Was nine workers that differed only in which success action they put (plan §10.2). They pass
// BOTH loader keys:
//
//   skipGlobalLoader  -> read only by utils/apiService/index.desktop.ts
//   hideLoader        -> read only by utils/apiService/index.native.ts
//
// Each interceptor ignores the other's key and each platform's dispatchers set only their own,
// so behaviour on both platforms is EXACTLY what it was.
//
// ⚠️ WHY NOT JUST UNIFY THE NAME — the obvious fix, deliberately not taken. `hideLoader` is
// already the more common spelling (116 sites vs 88) and web's own code uses it heavily, but
// **web's interceptor never reads it**: `apps/web/.../homeLayout.tsx:509` dispatches
// `getNewOrderData({ data: obj, hideLoader: true })`, web's saga reads `payload?.skipGlobalLoader`,
// and `apiService/index.desktop.ts` has zero references to `hideLoader`. So roughly forty
// `hideLoader` flags are DEAD on web today. Teaching web's interceptor to honour it would
// switch about seven `hideLoader: true` sites from "loader shows" to "loader hidden" — a
// user-visible change to the stable reference platform, which this adoption is not allowed to
// make. Unifying the name is a real fix and a separate, product-visible decision.
//
// Two deliberate changes from the nine-worker version, both sanctioned by §10.2:
//  1. `takeLatest` PER LIST (`watchListFetches`), not `takeEvery`: a second request for the same
//     list cancels the first, so a slow stale response can no longer overwrite a fresh one.
//     Requests for DIFFERENT lists still run concurrently, as before.
//  2. a failed request is swallowed and logged, exactly as v1's nine workers did — nothing in
//     either app reads a fetch flag on this slice (see MyOrderSlice/shared.ts), and the stale
//     list is left as it was.
// `getPreparedOrders` used to pass only `skipGlobalLoader`; it was web-only and web ignores
// `hideLoader`, so passing both is behaviour-preserving there and simply correct on mobile.

export interface ListFetcher {
  key: StoreKey;
  request: { type: string };
  success: (response: any) => { type: string; payload: any };
}

export const LIST_FETCHERS: readonly ListFetcher[] = [
  { key: "overviewOrdersInfo", request: getOverviewOrderData, success: getOverviewOrderDataSuccess },
  { key: "newOrdersInfo", request: getNewOrderData, success: getNewOrderDataSuccess },
  { key: "preparingOrdersInfo", request: getPreparingOrderData, success: getPreparingOrderDataSuccess },
  { key: "preparedOrdersInfo", request: getPreparedOrderData, success: getPreparedOrderDataSuccess },
  { key: "processedOrdersInfo", request: getProcessedOrderData, success: getProcessedOrderDataSuccess },
  { key: "dispatchOrdersInfo", request: getDispatchedOrderData, success: getDispatchedOrderDataSuccess },
  { key: "deliveredOrdersInfo", request: getDeliveredOrderData, success: getDeliveredOrderDataSuccess },
  { key: "cancelledOrdersInfo", request: getCancelledOrderData, success: getCancelledOrderDataSuccess },
  { key: "openOrdersInfo", request: getOpenOrderData, success: getOpenOrderDataSuccess },
];

export function* fetchList(fetcher: ListFetcher, action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.getMyOrders(),
      data: payload?.data,
      skipGlobalLoader: payload?.skipGlobalLoader,
      hideLoader: payload?.hideLoader
    } as any);
    console.log(`response ${fetcher.key}: `, response);
    yield put(fetcher.success(response));
  } catch (error: any) {
    console.log("error", error);
  }
}

/** Registered by both platform roots in place of nine `takeEvery` lines. */
export function* watchListFetches(): Generator<any, void, any> {
  for (const fetcher of LIST_FETCHERS) yield takeLatest(fetcher.request.type, fetchList, fetcher);
}

/**
 * The my-orders SEARCH worker — converged 2026-09-17 on web's body (D12, plan §7 phase 6 slice
 * 2), registered with `takeLatest` by both roots: a keystroke's request cancels the one before
 * it, so a slow stale result cannot overwrite a fresh one. A failure is logged and dropped, as
 * the lists do.
 *
 * The payload is the request body. Mobile's v1 `MyOrderAdvanceSearchMb` used to dispatch a
 * NESTED `{ sendObj, callback }` form — its only caller — and this worker unwrapped it; both
 * went at the phase-4 cutover (plan §8.1 ⑤).
 */
export function* getSearchOrdersData(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.getMyOrders(),
      data: action.payload,
    });
    yield put(getSearchOrdersSuccess(response));
  } catch (error: any) {
    console.log("error", error);
  }
}

export function* watchSearch(): Generator<any, void, any> {
  yield takeLatest(getSearchOrders.type, getSearchOrdersData);
}

/**
 * Mark items sold out for an aggregator order (and optionally reject it). Converged 2026-09-16 on
 * web's body: the callback receives the response, which the v2 reject sheet needs to tell an
 * API error (`code`) from success. Mobile's own caller (`OutOfStockPopup`) never read the
 * argument, so it is unchanged in effect.
 */
export function* soldOutItem(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.MYORDER.updateSoldOutItem(),
      data: payload.dataToSend,
    });
    if (response) {
      payload.callback(response);
      yield put(updateSoldOutSuccess(response));
    }
  } catch (error: any) {
    console.log(error);
  }
}
